import { toRefs, reactive, computed, watch, ref } from 'vue';
import { LayoutService } from '../services/layout.service';
import { useStates } from '../../router/states';


const { userState } = useStates();
const layoutService = LayoutService.getInstance();


let savedConfig;


const isAuthenticated = userState?.data?.value !== undefined;


const isInitializing = ref(true);


const updateReactiveValues = (config) => {


    menuMode.value = config.menuMode;
    colorScheme.value = config.colorScheme;
    componentTheme.value = config.componentTheme;
    scale.value = config.scale;
    menuTheme.value = config.menuTheme;
    topbarTheme.value = config.topbarTheme;
    menuProfilePosition.value = config.menuProfilePosition;
    inputStyle.value = config.inputStyle;
    ripple.value = config.ripple;
};

if (isAuthenticated && userState.data.value?.layoutConfig) {
    try {
        savedConfig = layoutService.loadConfig(userState.data.value.layoutConfig);

        
        if (!savedConfig || typeof savedConfig !== 'object') {

            savedConfig = layoutService.loadConfig();
        }
    } catch (error) {

        savedConfig = layoutService.loadConfig();
    }
} else {
    savedConfig = layoutService.loadConfig();

}

// Asegurarse de que todos los valores requeridos estén presentes
savedConfig.menuMode = savedConfig.menuMode || 'static';
savedConfig.colorScheme = savedConfig.colorScheme || 'light';
savedConfig.componentTheme = savedConfig.componentTheme || 'indigo';
savedConfig.scale = savedConfig.scale || 14;
savedConfig.menuTheme = savedConfig.menuTheme || 'light';
savedConfig.topbarTheme = savedConfig.topbarTheme || 'indigo';
savedConfig.menuProfilePosition = savedConfig.menuProfilePosition || 'end';
savedConfig.inputStyle = savedConfig.inputStyle || 'outlined';
savedConfig.ripple = savedConfig.ripple !== undefined ? savedConfig.ripple : false;

// Inicializar valores reactivos con valores vacíos primero
const menuMode = ref('');
const colorScheme = ref('');
const componentTheme = ref('');
const scale = ref(0);
const menuTheme = ref('');
const topbarTheme = ref('');
const menuProfilePosition = ref('');
const inputStyle = ref('');
const ripple = ref(false);

// Ahora actualizar con la configuración cargada
updateReactiveValues(savedConfig);

// Aplicar la escala inmediatamente
document.documentElement.style.fontSize = scale.value + 'px';

// Aplicar el input style a PrimeVue si está disponible
try {
    const primevue = window.PrimeVue || {};
    if (primevue.config) {
        primevue.config.inputStyle = inputStyle.value;
        primevue.config.ripple = ripple.value;
    }
} catch (e) {
}

const layoutConfig = reactive({
    menuMode,
    colorScheme,
    componentTheme,
    scale,
    menuTheme,
    topbarTheme,
    menuProfilePosition,
    inputStyle,
    ripple
});

// Watch for changes in layoutConfig and save them
const saveConfig = () => {
    const config = {
        menuMode: menuMode.value,
        colorScheme: colorScheme.value,
        componentTheme: componentTheme.value,
        scale: scale.value,
        menuTheme: menuTheme.value,
        topbarTheme: topbarTheme.value,
        menuProfilePosition: menuProfilePosition.value,
        inputStyle: inputStyle.value,
        ripple: ripple.value
    };
    layoutService.updateConfig(config);
};

// Observar cambios en el InitialData para actualizar los valores reactivos
watch(() => userState?.data?.value?.layoutConfig, (newValue) => {
    if (newValue) {
        try {
            const config = layoutService.loadConfig(newValue);
            updateReactiveValues(config);
            
            // Aplicar el tema inmediatamente
            if (window.applyThemeFromConfig) {
                window.applyThemeFromConfig(config);
            }
        } catch (error) {
        }
    }
}, { immediate: true, deep: true });

// Función para aplicar el tema actual utilizando la función global
const applyCurrentTheme = () => {
    // Verificar si hay datos en InitialData y usarlos si están disponibles
    if (isAuthenticated && userState.data.value?.layoutConfig) {
        try {
            const configFromInitialData = JSON.parse(userState.data.value.layoutConfig);
            
            // Actualizar los valores reactivos para mantenerlos sincronizados
            updateReactiveValues(configFromInitialData);
            
            // Aplicar el tema usando la función global
            if (window.applyThemeFromConfig) {
                window.applyThemeFromConfig(configFromInitialData);
                return; // Salir de la función después de aplicar el tema
            }
        } catch (error) {
        }
    }
    
    // Si no hay datos en InitialData o hubo un error, usar los valores reactivos
    if (window.applyThemeFromConfig) {
        const config = {
            menuMode: menuMode.value,
            colorScheme: colorScheme.value,
            componentTheme: componentTheme.value,
            scale: scale.value,
            menuTheme: menuTheme.value,
            topbarTheme: topbarTheme.value,
            menuProfilePosition: menuProfilePosition.value,
            inputStyle: inputStyle.value,
            ripple: ripple.value
        };
        
        window.applyThemeFromConfig(config);
    } else {
        
        // Método alternativo si la función global no está disponible
        const themeLink = document.getElementById('theme-link');
        if (themeLink) {
            const themeLinkHref = themeLink.getAttribute('href');
            const currentColorScheme = themeLinkHref.includes('theme-dark') ? 'dark' : 'light';
            
            // Si el esquema de color actual no coincide con el configurado, cambiarlo
            if (currentColorScheme !== colorScheme.value) {
                const newColorScheme = 'theme-' + colorScheme.value;
                const oldColorScheme = 'theme-' + (colorScheme.value === 'dark' ? 'light' : 'dark');
                const newHref = themeLinkHref.replace(oldColorScheme, newColorScheme);
                
                replaceLink(themeLink, newHref, () => {
                    // Luego aplicar el tema del componente
                    const updatedThemeLink = document.getElementById('theme-link');
                    if (updatedThemeLink) {
                        const updatedHref = updatedThemeLink.getAttribute('href');
                        const currentThemeMatch = updatedHref.match(/\/([^\/]+)\/theme\.css$/);
                        
                        if (currentThemeMatch && currentThemeMatch[1] !== componentTheme.value) {
                            const newComponentHref = updatedHref.replace(currentThemeMatch[1], componentTheme.value);
                            replaceLink(updatedThemeLink, newComponentHref, () => {
                                // Component theme applied successfully
                            });
                        }
                    }
                });
            } else {
                // Si el esquema de color ya es correcto, solo verificar el tema del componente
                const currentThemeMatch = themeLinkHref.match(/\/([^\/]+)\/theme\.css$/);
                
                if (currentThemeMatch && currentThemeMatch[1] !== componentTheme.value) {
                    const newComponentHref = themeLinkHref.replace(currentThemeMatch[1], componentTheme.value);
                    replaceLink(themeLink, newComponentHref, () => {
                        // Component theme applied successfully
                    });
                }
            }
        }
    }
};

// Aplicar el tema actual después de un breve retraso para asegurar que el DOM esté listo
setTimeout(() => {
    applyCurrentTheme();
}, 300);

watch([menuMode, colorScheme, componentTheme, scale, menuTheme, topbarTheme, menuProfilePosition, inputStyle, ripple], () => {
    // Solo guardar si no estamos en la inicialización
    if (!isInitializing.value) {
        saveConfig();
    }
});


setTimeout(() => {
    isInitializing.value = false;

    
    // Aplicar el tema una última vez para asegurarnos de que esté correctamente configurado
    applyCurrentTheme();
}, 2000);

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    rightHistoryActive:  false,
    topbarMenuActive: false,
    sidebarActive: false,
    anchored: false,
    activeMenuItem: null,
    overlaySubmenuActive: false,
    menuProfileActive: false
});

export function useLayout() {
    const onMenuProfileToggle = () => {
        layoutState.menuProfileActive = !layoutState.menuProfileActive;
    };

    const changeColorScheme = (colorScheme) => {
        // Usar la función global si está disponible
        if (window.applyThemeFromConfig) {
            // Verificar que layoutConfig.colorScheme sea un objeto reactivo
            if (typeof layoutConfig.colorScheme === 'object' && layoutConfig.colorScheme !== null) {
                layoutConfig.colorScheme.value = colorScheme;
            } else {
                // Si no es un objeto reactivo, asignar directamente
                layoutConfig.colorScheme = colorScheme;
            }
            
            // Actualizar el menuTheme basado en el colorScheme
            if (colorScheme === 'dark') {
                if (typeof layoutConfig.menuTheme === 'object' && layoutConfig.menuTheme !== null) {
                    layoutConfig.menuTheme.value = 'dark';
                } else {
                    layoutConfig.menuTheme = 'dark';
                }
                menuTheme.value = 'dark';
            } else {
                if (typeof layoutConfig.menuTheme === 'object' && layoutConfig.menuTheme !== null) {
                    layoutConfig.menuTheme.value = 'light';
                } else {
                    layoutConfig.menuTheme = 'light';
                }
                menuTheme.value = 'light';
            }
            
            // Aplicar el tema usando la función global
            const config = {
                colorScheme: colorScheme,
                componentTheme: typeof layoutConfig.componentTheme === 'object' ? layoutConfig.componentTheme.value : layoutConfig.componentTheme,
                scale: typeof layoutConfig.scale === 'object' ? layoutConfig.scale.value : layoutConfig.scale,
                menuTheme: typeof layoutConfig.menuTheme === 'object' ? layoutConfig.menuTheme.value : layoutConfig.menuTheme,
                topbarTheme: typeof layoutConfig.topbarTheme === 'object' ? layoutConfig.topbarTheme.value : layoutConfig.topbarTheme,
                menuProfilePosition: typeof layoutConfig.menuProfilePosition === 'object' ? layoutConfig.menuProfilePosition.value : layoutConfig.menuProfilePosition,
                inputStyle: typeof layoutConfig.inputStyle === 'object' ? layoutConfig.inputStyle.value : layoutConfig.inputStyle,
                ripple: typeof layoutConfig.ripple === 'object' ? layoutConfig.ripple.value : layoutConfig.ripple
            };
            
            window.applyThemeFromConfig(config);
            
            // Guardar la configuración actualizada solo si no estamos inicializando
            if (!isInitializing.value) {
                saveConfig();
            }
        } else {
            // Método alternativo si la función global no está disponible
            const themeLink = document.getElementById('theme-link');
            const themeLinkHref = themeLink.getAttribute('href');
            const currentColorSchemeValue = typeof layoutConfig.colorScheme === 'object' ? layoutConfig.colorScheme.value : layoutConfig.colorScheme;
            const currentColorScheme = 'theme-' + currentColorSchemeValue.toString();
            const newColorScheme = 'theme-' + colorScheme;
            const newHref = themeLinkHref.replace(currentColorScheme, newColorScheme);

            replaceLink(themeLink, newHref, () => {
                // Actualizar los valores reactivos directamente
                if (typeof layoutConfig.colorScheme === 'object' && layoutConfig.colorScheme !== null) {
                    layoutConfig.colorScheme.value = colorScheme;
                } else {
                    layoutConfig.colorScheme = colorScheme;
                }
                
                // Actualizar el menuTheme basado en el colorScheme
                if (colorScheme === 'dark') {
                    if (typeof layoutConfig.menuTheme === 'object' && layoutConfig.menuTheme !== null) {
                        layoutConfig.menuTheme.value = 'dark';
                    } else {
                        layoutConfig.menuTheme = 'dark';
                    }
                    menuTheme.value = 'dark';
                } else {
                    if (typeof layoutConfig.menuTheme === 'object' && layoutConfig.menuTheme !== null) {
                        layoutConfig.menuTheme.value = 'light';
                    } else {
                        layoutConfig.menuTheme = 'light';
                    }
                    menuTheme.value = 'light';
                }
                
                // Guardar la configuración actualizada solo si no estamos inicializando
                if (!isInitializing.value) {
                    saveConfig();
                }
            });
        }
    };
    const replaceLink = (linkElement, href, onComplete) => {
        if (!linkElement || !href) {
            return;
        }

        const id = linkElement.getAttribute('id');
        const cloneLinkElement = linkElement.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

        cloneLinkElement.addEventListener('load', () => {
            linkElement.remove();

            const element = document.getElementById(id); // re-check
            element && element.remove();

            cloneLinkElement.setAttribute('id', id);
            onComplete && onComplete();
        });
    };
    const setScale = (scale) => {
        layoutConfig.scale = scale;
    };

    const setActiveMenuItem = (item) => {
        layoutState.activeMenuItem = item.value || item;
    };

    const changeMenuMode = (mode) => {

        
    
        const prevMode = layoutConfig.menuMode.value;
        
    
        layoutConfig.menuMode.value = mode;
        
        // Manejar casos especiales para ciertos modos
    
        // Solo actualizar el estado para el modo static si explícitamente se selecciona ese modo
        if (mode === 'static' && prevMode !== 'static') {
            layoutState.staticMenuDesktopInactive.value = false;
        }
        
    
        if (window.applyThemeFromConfig) {
            const config = {
                menuMode: layoutConfig.menuMode.value,
                colorScheme: layoutConfig.colorScheme.value,
                componentTheme: layoutConfig.componentTheme.value,
                scale: layoutConfig.scale.value,
                menuTheme: layoutConfig.menuTheme.value,
                topbarTheme: layoutConfig.topbarTheme.value,
                menuProfilePosition: layoutConfig.menuProfilePosition.value,
                inputStyle: layoutConfig.inputStyle.value,
                ripple: layoutConfig.ripple.value
            };

            window.applyThemeFromConfig(config);
        }
    };

    const onMenuToggle = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth > 991) {
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
        }
    };
    const onTopbarMenuToggle = () => {
        layoutState.topbarMenuActive = !layoutState.topbarMenuActive;
    };
    const openRightSidebar = () => {
        layoutState.rightMenuActive = true;
    };

    const onProfileSidebarToggle = () => {
        layoutState.rightMenuActive = !layoutState.rightMenuActive;
    };

    const openRightHistorySidebar = () => {
        layoutState.rightHistoryActive = true;
    };

    const onProfileHistorySidebarToggle = () => {
        layoutState.rightHistoryActive = !layoutState.rightHistoryActive;
    };

    const onConfigSidebarToggle = () => {
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
    };

    const isSidebarActive = computed(() => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive || layoutState.overlaySubmenuActive);

    const isDarkTheme = computed(() => layoutConfig.darkTheme);

    const isDesktop = computed(() => window.innerWidth > 991);

    const isSlim = computed(() => layoutConfig.menuMode === 'slim');
    const isSlimPlus = computed(() => layoutConfig.menuMode === 'slim-plus');

    const isHorizontal = computed(() => layoutConfig.menuMode === 'horizontal');

    const isOverlay = computed(() => layoutConfig.menuMode === 'overlay');

    const isRightHistoryActive= computed(() => layoutState.rightHistoryActive);

    return {
        layoutConfig: toRefs(layoutConfig),
        layoutState: toRefs(layoutState),
        onMenuToggle,
        onProfileSidebarToggle,
        onProfileHistorySidebarToggle,
        onConfigSidebarToggle,
        onTopbarMenuToggle,
        openRightHistorySidebar,
        replaceLink,
        changeColorScheme,
        changeMenuMode,
        setScale,
        setActiveMenuItem,
        isSidebarActive,
        isDarkTheme,
        isDesktop,
        isSlim,
        isSlimPlus,
        isHorizontal,
        isOverlay,
        isRightHistoryActive
    };
}
